import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { RegisterDateType } from "../../types"
import { APPROVAL, DELIVERED } from "../vars"
import { calculateRegister } from "./calculateRegister"

type ExportExcelDateRange = {
  data: RegisterDateType[]
  dateFilter: string
  companyName?: string
}

export const exportExcelDateRange = async ({
  data,
  dateFilter
}: ExportExcelDateRange) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "SO ID#",
    "DATE",
    "CUSTOMER",
    "APPROVAL",
    "DELIVERED",
    "QTY ORDER",
    "QTY DELIVERED",
    "QTY BACK ORDER",
    "TOTAL SALES",
    "TOTAL PAYMENT",
    "BALANCE OWING"
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `register-date-range-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

export const makeTitle = (worksheet: Worksheet, dateFilter: string) => {
  worksheet.mergeCells(1, 1, 1, 12)
  const purchaseCell = worksheet.getCell(1, 1)
  purchaseCell.value = "SALES"
  purchaseCell.font = { bold: true, size: 15 }
  purchaseCell.alignment = {
    vertical: "middle",
    horizontal: "center"
  }

  worksheet.mergeCells(2, 1, 2, 12)
  const registerCell = worksheet.getCell(2, 1)
  registerCell.value = "REGISTER BY DATE RANGE"
  registerCell.font = { bold: true, size: 15 }
  registerCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }

  worksheet.mergeCells(3, 1, 3, 12)
  const dateCell = worksheet.getCell(3, 1)
  dateCell.value = `DATE: ${dateFilter}`
  dateCell.font = { bold: true, size: 13 }
  dateCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }

  worksheet.getColumn(2).width = 20
}

export const makeRows = (
  data: ExportExcelDateRange["data"],
  worksheet: Worksheet
) => {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      item.trx_code,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.customer_name,
      APPROVAL[item.approval_status],
      DELIVERED[item.so_delivery_status],
      item.qty_order,
      item.qty_delivered,
      item.qty_back_order,
      item.total,
      item.total_payment,
      item.balance_owing
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = calculateRegister(data)

  // summary
  const sumarryQtyOrder = worksheet.addRow(
    Array(10).fill("").concat(["SUMMARY QTY ORDER", calculate.qty_order])
  )
  const sumarryQtyShipped = worksheet.addRow(
    Array(10).fill("").concat(["SUMMARY QTY SHIPPED", calculate.qty_delivered])
  )
  const sumarryPayment = worksheet.addRow(
    Array(10).fill("").concat(["SUMMARY PAYMENT", calculate.payment])
  )
  const sumarryBalance = worksheet.addRow(
    Array(10).fill("").concat(["SUMMARY BALANCE", calculate.balance])
  )

  sumarryQtyOrder.getCell(11).font = { bold: true }
  sumarryQtyShipped.getCell(12).font = { bold: true }
  sumarryPayment.getCell(11).font = { bold: true }
  sumarryPayment.getCell(12).font = { bold: true }
  sumarryBalance.getCell(11).font = { bold: true }
  sumarryBalance.getCell(12).font = { bold: true }
}
